import { Navigate } from 'react-router-dom';
import { lazy } from 'react';
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import MainLayout from './layouts/main';
import store from './store';
import { getApplicantRoutes } from './utils/routeUtils/applicant';
import { getAdminRoutes } from './utils/routeUtils/admin';
import { getFairRoutes } from './utils/routeUtils/fair';

// Lazy load all pages
const Settings = lazy(() => import('./pages/Admin/Settings'));
const Terms = lazy(() => import('./pages/Admin/Settings/Term'));
const Disciplines = lazy(() => import('./pages/Admin/Settings/Disciplines'));
const Category = lazy(() => import('./pages/Admin/Settings/Category'));
const Trip = lazy(() => import('./pages/Admin/Settings/Trip'));
const TravelSettings = lazy(() => import('./pages/Admin/Settings/Travel'));
const Fee = lazy(() => import('./pages/Admin/Settings/Fee'));
const AdminPayment = lazy(() => import('./pages/Admin/Payment'));
const ProjectPayment = lazy(() => import('./pages/Admin/Payment/Project'));
const ParticipantPayment = lazy(() => import('./pages/Admin/Payment/Participant'));
const TripPayment = lazy(() => import('./pages/Admin/Payment/Trip'));
const TravelPayment = lazy(() => import('./pages/Admin/Payment/Travel'));
const FairOperation = lazy(() => import('./pages/Admin/FairOperation'));
const FairFairOperation = lazy(() => import('./pages/Admin/FairOperation/Fair'));
const RequestFairOperation = lazy(() => import('./pages/Admin/FairOperation/Request'));
const FairAdmin = lazy(() => import('./pages/Fair/FairAdmin'));
const JudgeRegistration = lazy(() => import('./pages/Register/JudgeRegister/JudgeRegistration'));
const FairRegistration = lazy(() => import('./pages/Register/FairRegistration'));
const GeneralUserRegistration = lazy(() => import('./pages/Register/GeneralUserRegistration'));
const ResetForm = lazy(() => import('./components/authentication/resetPassword/ResetForm'));
const NotFound = lazy(() => import('./pages/Page404'));
const FairProfile = lazy(() => import('./pages/Fair/Profile'));
const School = lazy(() => import('./pages/Application/School'));
const Travel = lazy(() => import('./pages/Application/Travel'));
const TripForGeneralUser = lazy(() => import('./pages/Application/Trip'));
const PresentationForGeneralUser = lazy(() => import('./pages/Application/Presentation'));
const Projects = lazy(() => import('./pages/Application/Projects'));
const Profile = lazy(() => import('./pages/Profile'));
const PaymentForProjects = lazy(() => import('./pages/Payments/CheckoutWidget'));
const PaymentForParticipants = lazy(() => import('./pages/Payments/PaymentForParticipants'));
const PaymentForTrips = lazy(() => import('./pages/Payments/PaymentForTrips'));
const ParticipantOperation = lazy(() => import('./pages/Admin/ParticipantOperation'));
const SchoolOperation = lazy(() => import('./pages/Admin/SchoolOperation'));
const ProjectOperation = lazy(() => import('./pages/Admin/ProjectOperation'));
const Participation = lazy(() => import('./pages/Admin/ParticipantOperation/Participation'));
const ParticipationForRegularUser = lazy(() => import('./pages/Application/Participation'));
const ParticipantForRegularUser = lazy(() => import('./pages/Application/Participation/Participant'));
const InvitationLetterForRegularUser = lazy(() => import('./pages/Application/Participation/InvitationLetter'));
const SupportLetterForRegularUser = lazy(() => import('./pages/Application/Participation/SupportLetter'));
const VipForRegularUser = lazy(() => import('./pages/Application/Participation/Vip'));
const InvitationLetterForAdmin = lazy(() => import('./pages/Admin/ParticipantOperation/InvitationLetter'));
const ReportsForParticipantOperation = lazy(() => import('./pages/Admin/ParticipantOperation/Reports'));
const TripForAdmin = lazy(() => import('./pages/Admin/ParticipantOperation/Trip'));
const TravelForAdmin = lazy(() => import('./pages/Admin/ParticipantOperation/Travel'));
const UserManagement = lazy(() => import('./pages/Admin/UserManagement'));
const DataRecovery = lazy(() => import('./pages/Admin/DataRecovery'));
const ReviewOperation = lazy(() => import('./pages/Admin/ReviewOperation'));
const Communication = lazy(() => import('./pages/Admin/Communication/Communication.jsx'));
const ReviewRO = lazy(() => import('./pages/Admin/ReviewOperation/Review'));
const ReviewerReportRO = lazy(() => import('./pages/Admin/ReviewOperation/ReviewerReport'));
const MultipleFinalist = lazy(() => import('./pages/Admin/ReviewOperation/MultipleFinalist'));
const InfoPO = lazy(() => import('./pages/Admin/ProjectOperation/Info'));
const ReportPO = lazy(() => import('./pages/Admin/ProjectOperation/Report'));
const RoleUM = lazy(() => import('./pages/Admin/UserManagement/Roles'));
const UserUM = lazy(() => import('./pages/Admin/UserManagement/Users'));
const ReviewAdmin = lazy(() => import('./pages/Reviewer'));
const PerformanceApplicant = lazy(() => import('./pages/Application/Performance'));
const PerformanceOperation = lazy(() => import('./pages/Admin/PerformanceOperation'));
const Home = lazy(() => import('./pages/Application/Home'));
const JudgeOperation = lazy(() => import('./pages/Admin/JudgeOperation'));
const Session = lazy(() => import('./pages/Admin/JudgeOperation/Session'));
const Judge = lazy(() => import('./pages/Admin/JudgeOperation/Judge'));
const Group = lazy(() => import('./pages/Admin/JudgeOperation/Group'));
const Judging = lazy(() => import('./pages/Admin/JudgeOperation/Judging'));
const ScoreKeeper = lazy(() => import('./pages/ScoreKeeper'));
const JudgingForm = lazy(() => import('./pages/Admin/JudgeOperation/JudgingForm'));
const JudgeAdmin = lazy(() => import('./pages/Judge'));
const Robotics = lazy(() => import('./pages/Admin/JudgeOperation/Robotics'));
const JudgingSession = lazy(() => import('./pages/Application/JudgingSession'));
const AwardOperation = lazy(() => import('./pages/Admin/AwardOperation'));
const AwardRO = lazy(() => import('./pages/Admin/AwardOperation/Award'));
const AwardReportRO = lazy(() => import('./pages/Admin/AwardOperation/Report'));
const CertificateRO = lazy(() => import('./pages/Admin/AwardOperation/Certificate'));
const JudgeCertificateAdmin = lazy(() => import('./pages/Judge/Certificate'));
const GuidelineOperation = lazy(() => import('./pages/Admin/GuidelineOperation'));
const DocumentOperation = lazy(() => import('./pages/Admin/DocumentOperation'));
const SurveyOperation = lazy(() => import('./pages/Admin/SurveyOperation/Survey'));
const SOR = lazy(() => import('./pages/Admin/SurveyOperation/Report/Report'));
const Documents = lazy(() => import('./pages/Documents'));
const PaymentLink = lazy(() => import('./pages/PaymentLink/PaymentLink'));
const PartnerFairOperation = lazy(() => import('./pages/Admin/PartnerFairOperation'));
const ConfigPartnerFairOperation = lazy(() => import('./pages/Admin/PartnerFairOperation/Config/Config'));
const ReportPartnerFairOperation = lazy(() => import('./pages/Admin/PartnerFairOperation/Report/Report'));
const ApplicantPayment = lazy(() => import('./pages/Application/Payment/Payment'));
const Survey = lazy(() => import('./pages/Survey/Survey'));
const CodingOperation = lazy(() => import('./pages/Admin/CodingOperation'));
const PaymentForTravels = lazy(() => import('./pages/Payments/PaymentForTravels'));
const LoginWithLinkView = lazy(() => import('./pages/LoginWithLinkView'));
const Certificates = lazy(() => import('./pages/Application/Participation/Certificates'));

export function getRoutes(module_access, isProfileOK) {
  let allModules = [];
  module_access.forEach((item) => {
    allModules = [...allModules, ...item.modules];
  });
  // APPLICANT
  const {
    isPerformance,
    isParticipation,
    isHome,
    isDocumentForApplicant,
    isParticipantFeature,
    isInvitationLetterFeature,
    isSupportLetterFeature,
    isVipFeature,
    isProject,
    isSchool,
    isTravel,
    isTrip,
    isPresentation,
    isProfile,
    isApplicantJudging,
    isApplicantPayment
  } = getApplicantRoutes(allModules);

  // ADMIN
  const {
    isPayment,
    isParticipantPaymentFeature,
    isProjectPaymentFeature,
    isTripPaymentFeature,
    isTravelPaymentFeature,
    isSchoolOperation,
    isPerformanceOperation,
    isParticipantOperation,
    isParticipationParticipantOpFeature,
    isInvitationLettersParticipantOpFeature,
    isVipParticipantOpFeature,
    isTripParticipantOpFeature,
    isTravelParticipantOpFeature,
    isFairOperation,
    isFairFairOpFeature,
    isProfileFairOpFeature,
    isRequestFairOpFeature,
    isSettings,
    isFeeSettingsFeature,
    isTripSettingsFeature,
    isTravelSettingsFeature,
    isCategorySettingsFeature,
    isDisciplineSettingsFeature,
    isTermSettingsFeature,
    isUserManagement,
    isRoleUMFeature,
    isUserUMFeature,
    isProjectOperation,
    isInfoPOFeature,
    isReportPOFeature,
    isDataRecovery,
    isCommunication,
    isReviewOperation,
    isReviewRO,
    isReviewerReportRO,
    isMultipleFinalistRO,
    isJudgeOperation,
    isSessionJOFeature,
    isJudgeJOFeature,
    isGroupJOFeature,
    isJudgingJOFeature,
    isJudgingFormJOFeature,
    isRoboticsJOFeature,
    isAwardOperation,
    isAwardRO,
    isAwardReportRO,
    isCertificateRO,
    isGuidelineOperation,
    isDocumentOperation,
    isPFO,
    isConfigPFO,
    isReportPFO,
    isSurvey,
    isCodingOperation,
    isSOS,
    isSOR
  } = getAdminRoutes(allModules);

  // FAIR
  const {
    isFairAdmin,
    isProfileFairAdminFeature,
    isRequestFairAdminFeature,
    isDocumentFairAdminFeature
  } = getFairRoutes(allModules);

  // REVIEWER
  const isReviewAdmin = allModules.findIndex((x) => x.module_access_key === 'reviewer');

  // JUDGE
  const isJudge = allModules.findIndex((x) => x.module_access_key === 'judge');
  const isJudgeCertificate =
    isJudge !== -1
      ? allModules[isJudge].features.findIndex((x) => x.feature_access_key === 'certificate')
      : -1;

  // JUDGE
  const isScorekeeper = allModules.findIndex((x) => x.module_access_key === 'scorekeeper');

  if (isProfileOK) {
    store.dispatch({
      type: 'SET_ROUTE',
      payload: {
        isPerformance,
        isHome,
        isParticipation,
        isParticipantFeature,
        isInvitationLetterFeature,
        isSupportLetterFeature,
        isVipFeature,
        isProject,
        isSchool,
        isTravel,
        isTrip,
        isPresentation,
        isProfile,
        isPayment,
        isParticipantPaymentFeature,
        isProjectPaymentFeature,
        isTripPaymentFeature,
        isTravelPaymentFeature,
        isSchoolOperation,
        isParticipantOperation,
        isParticipationParticipantOpFeature,
        isInvitationLettersParticipantOpFeature,
        isVipParticipantOpFeature,
        isTripParticipantOpFeature,
        isTravelParticipantOpFeature,
        isFairOperation,
        isProfileFairOpFeature,
        isFairFairOpFeature,
        isRequestFairOpFeature,
        isSettings,
        isFeeSettingsFeature,
        isTripSettingsFeature,
        isTravelSettingsFeature,
        isCategorySettingsFeature,
        isDisciplineSettingsFeature,
        isTermSettingsFeature,
        isUserManagement,
        isRoleUMFeature,
        isUserUMFeature,
        isProjectOperation,
        isFairAdmin,
        isDataRecovery,
        isCommunication,
        isReviewOperation,
        isReviewRO,
        isReviewerReportRO,
        isMultipleFinalistRO,
        isProfileFairAdminFeature,
        isRequestFairAdminFeature,
        isReviewAdmin,
        isInfoPOFeature,
        isReportPOFeature,
        isPerformanceOperation,
        isDocumentFairAdminFeature,
        isDocumentForApplicant,
        isJudgeOperation,
        isSessionJOFeature,
        isJudgeJOFeature,
        isGroupJOFeature,
        isJudgingJOFeature,
        isJudgingFormJOFeature,
        isJudge,
        isRoboticsJOFeature,
        isScorekeeper,
        isApplicantJudging,
        isAwardOperation,
        isAwardRO,
        isAwardReportRO,
        isCertificateRO,
        isJudgeCertificate,
        isGuidelineOperation,
        isDocumentOperation,
        isPFO,
        isConfigPFO,
        isReportPFO,
        isApplicantPayment,
        isSurvey,
        isCodingOperation,
        isSOS,
        isSOR
      }
    });
  }

  return [
    {
      path: '/main',
      element: <MainLayout />,
      children: [{ path: '', element: <Navigate to="/main" replace /> }]
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { path: '', element: <Navigate to="/dashboard/home" replace /> },
        isHome !== -1 && { path: 'home', element: <Home /> },
        isDocumentForApplicant !== -1 && { path: 'documents', element: <Documents /> },
        isSchool !== -1 && { path: 'school', element: <School /> },
        isTravel !== -1 && { path: 'travel', element: <Travel /> },
        isTrip !== -1 && { path: 'trip', element: <TripForGeneralUser /> },
        isPresentation !== -1 && { path: 'presentation', element: <PresentationForGeneralUser /> },
        isPerformance !== -1 && { path: 'performance', element: <PerformanceApplicant /> },
        isApplicantPayment !== -1 && { path: 'payment', element: <ApplicantPayment /> },
        isParticipation !== -1 && {
          path: 'participation',
          element: <ParticipationForRegularUser />
        },
        isParticipantFeature !== -1 && {
          path: 'participation/participant',
          element: <ParticipantForRegularUser />
        },
        isInvitationLetterFeature !== -1 && {
          path: 'participation/invitation_letter',
          element: <InvitationLetterForRegularUser />
        },
        isSupportLetterFeature !== -1 && {
          path: 'participation/support_letter',
          element: <SupportLetterForRegularUser />
        },
        isVipFeature !== -1 && {
          path: 'participation/vip',
          element: <VipForRegularUser />
        },
        {
          path: 'participation/certificate',
          element: <Certificates />
        },
        isApplicantJudging !== -1 && { path: 'judging_session', element: <JudgingSession /> },
        isProject !== -1 && { path: 'projects', element: <Projects /> },
        { path: 'profile', element: <Profile /> },
        { path: 'payments_for_projects', element: <PaymentForProjects /> },
        { path: 'payments_for_participants', element: <PaymentForParticipants /> },
        { path: 'payments_for_trips', element: <PaymentForTrips /> },
        { path: 'payments_for_travels', element: <PaymentForTravels /> },
        { path: 'survey', element: <Survey /> },
        { path: '*', element: <Navigate to="/main" /> }
      ]
    },
    {
      path: '/admin',
      element: <DashboardLayout />,
      children: [
        { path: '', element: <Navigate to="/admin/school_operation" replace /> },
        isSettings !== -1 && { path: 'settings', element: <Settings /> },
        isSchoolOperation !== -1 && { path: 'school_operation', element: <SchoolOperation /> },
        isPerformanceOperation !== -1 && {
          path: 'performance_operation',
          element: <PerformanceOperation />
        },
        isProjectOperation !== -1 && { path: 'project_operation', element: <ProjectOperation /> },
        isInfoPOFeature !== -1 && { path: 'project_operation/info', element: <InfoPO /> },
        isReportPOFeature !== -1 && {
          path: 'project_operation/report',
          element: <ReportPO />
        },

        isTermSettingsFeature !== -1 && { path: 'settings/terms', element: <Terms /> },
        isDisciplineSettingsFeature !== -1 && {
          path: 'settings/discipline',
          element: <Disciplines />
        },
        isCategorySettingsFeature !== -1 && { path: 'settings/category', element: <Category /> },
        isTripSettingsFeature !== -1 && { path: 'settings/trip', element: <Trip /> },
        isTravelSettingsFeature !== -1 && { path: 'settings/travel', element: <TravelSettings /> },
        isFeeSettingsFeature !== -1 && { path: 'settings/fee', element: <Fee /> },
        isPayment !== -1 && { path: 'payment', element: <AdminPayment /> },
        isSurvey !== -1 && { path: 'survey_operation', element: <SurveyOperation /> },
        isCodingOperation !== -1 && { path: 'coding_operation', element: <CodingOperation /> },
        isSOS !== -1 && { path: 'survey_operation/survey', element: <SurveyOperation /> },
        isSOR !== -1 && { path: 'survey_operation/report', element: <SOR /> },
        isProjectPaymentFeature !== -1 && {
          path: 'payment/projects',
          element: <ProjectPayment />
        },
        isParticipantPaymentFeature !== -1 && {
          path: 'payment/participants',
          element: <ParticipantPayment />
        },
        isTripPaymentFeature !== -1 && {
          path: 'payment/trip',
          element: <TripPayment />
        },
        isTravelPaymentFeature !== -1 && {
          path: 'payment/travel',
          element: <TravelPayment />
        },
        isJudgeOperation !== -1 && {
          path: 'judge_operation',
          element: <JudgeOperation />
        },
        isSessionJOFeature !== -1 && {
          path: 'judge_operation/session',
          element: <Session />
        },
        isJudgeJOFeature !== -1 && {
          path: 'judge_operation/judge',
          element: <Judge />
        },
        isGroupJOFeature !== -1 && {
          path: 'judge_operation/group',
          element: <Group />
        },
        isJudgingJOFeature !== -1 && {
          path: 'judge_operation/judging',
          element: <Judging />
        },
        isJudgingFormJOFeature !== -1 && {
          path: 'judge_operation/form',
          element: <JudgingForm />
        },
        isRoboticsJOFeature !== -1 && {
          path: 'judge_operation/robotics_competition',
          element: <Robotics />
        },
        isJudge !== -1 && {
          path: 'judge',
          element: <JudgeAdmin />
        },
        isJudgeCertificate !== -1 && {
          path: 'certificate',
          element: <JudgeCertificateAdmin />
        },
        isScorekeeper !== -1 && {
          path: 'scorekeeper',
          element: <ScoreKeeper />
        },
        // TODO control
        isUserManagement !== -1 && { path: 'fair_operation', element: <FairOperation /> },
        isFairOperation !== -1 && { path: 'fair_operation/fair', element: <FairFairOperation /> },
        isRequestFairOpFeature !== -1 && {
          path: 'fair_operation/request',
          element: <RequestFairOperation />
        },
        isPFO !== -1 && { path: 'partner_fair_operation', element: <PartnerFairOperation /> },
        isConfigPFO !== -1 && {
          path: 'partner_fair_operation/config',
          element: <ConfigPartnerFairOperation />
        },
        isReportPFO !== -1 && {
          path: 'partner_fair_operation/report',
          element: <ReportPartnerFairOperation />
        },
        isUserManagement !== -1 && { path: 'user_management', element: <UserManagement /> },
        isRoleUMFeature !== -1 && { path: 'user_management/role', element: <RoleUM /> },
        isUserUMFeature !== -1 && { path: 'user_management/user', element: <UserUM /> },
        isParticipantOperation !== -1 && {
          path: 'participant_operation',
          element: <ParticipantOperation />
        },
        isParticipationParticipantOpFeature !== -1 && {
          path: 'participant_operation/participation',
          element: <Participation />
        },
        {
          path: 'participant_operation/reports',
          element: <ReportsForParticipantOperation />
        },
        isInvitationLettersParticipantOpFeature !== -1 && {
          path: 'participant_operation/invitation_letter',
          element: <InvitationLetterForAdmin />
        },
        isTripParticipantOpFeature !== -1 && {
          path: 'participant_operation/trip',
          element: <TripForAdmin />
        },
        isTravelParticipantOpFeature !== -1 && {
          path: 'participant_operation/travel',
          element: <TravelForAdmin />
        },
        isReviewOperation !== -1 && {
          path: 'review_operation',
          element: <ReviewOperation />
        },
        isReviewRO !== -1 && {
          path: 'review_operation/review',
          element: <ReviewRO />
        },
        isReviewerReportRO !== -1 && {
          path: 'review_operation/report',
          element: <ReviewerReportRO />
        },
        isMultipleFinalistRO !== -1 && {
          path: 'review_operation/multiple_finalist',
          element: <MultipleFinalist />
        },
        isAwardOperation !== -1 && {
          path: 'award_operation',
          element: <AwardOperation />
        },
        isGuidelineOperation !== -1 && {
          path: 'guideline_operation',
          element: <GuidelineOperation />
        },
        isDocumentOperation !== -1 && {
          path: 'document_operation',
          element: <DocumentOperation />
        },
        isAwardRO !== -1 && {
          path: 'award_operation/award',
          element: <AwardRO />
        },
        isAwardReportRO !== -1 && {
          path: 'award_operation/report',
          element: <AwardReportRO />
        },
        isCertificateRO !== -1 && {
          path: 'award_operation/certificate_template',
          element: <CertificateRO />
        },
        isDataRecovery !== -1 && {
          path: 'data_recovery',
          element: <DataRecovery />
        },
        isCommunication !== -1 && {
          path: 'communication',
          element: <Communication />
        },
        { path: '*', element: <Navigate to="/404" /> }
      ]
    },
    {
      path: '/fair',
      element: <DashboardLayout />,
      children: [
        { path: '', element: <Navigate to="/fair/fair_admin" replace /> },
        isRequestFairAdminFeature !== -1 && { path: 'fair_admin', element: <FairAdmin /> },
        isProfileFairAdminFeature !== -1 && { path: 'fair_profile', element: <FairProfile /> },
        isDocumentFairAdminFeature !== -1 && { path: 'documents', element: <Documents /> },
        { path: '*', element: <Navigate to="/main" /> }
      ]
    },
    {
      path: '/reviewer',
      element: <DashboardLayout />,
      children: [
        { path: '', element: <Navigate to="/reviewer/review_admin" replace /> },
        isReviewAdmin !== -1 && { path: 'review_admin', element: <ReviewAdmin /> },
        { path: '*', element: <Navigate to="/404" /> }
      ]
    },
    {
      path: '/project_reviewer',
      element: <DashboardLayout />,
      children: [
        { path: '', element: <Navigate to="/admin/review_operation" replace /> },
        isReviewOperation !== -1 && {
          path: 'review_operation',
          element: <ReviewOperation />
        },
        isReviewRO !== -1 && {
          path: 'review_operation/review',
          element: <ReviewRO />
        },
        isReviewerReportRO !== -1 && {
          path: 'review_operation/report',
          element: <ReviewerReportRO />
        },
        { path: '*', element: <Navigate to="/404" /> }
      ]
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'judge_registration', element: <JudgeRegistration /> },
        { path: 'fair_registration', element: <FairRegistration /> },
        { path: 'general_user_registration', element: <GeneralUserRegistration /> },
        { path: 'login_with_link:user_id', element: <LoginWithLinkView /> },
        { path: 'reset', element: <ResetForm /> },
        { path: '404', element: <NotFound /> },
        { path: '', element: <Navigate to="/main" /> },
        { path: '*', element: <Navigate to="/404" /> },
        { path: 'payment', element: <PaymentLink /> },
        { path: 'survey', element: <Survey /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ];
}
